import { AttackPattern, PatternState } from '../AttackPattern.js';

/**
 * Déjà Vu (The Chronophage) — records the player's actual path as a
 * breadcrumb trail (a position sample every 0.22s, starting at telegraph),
 * then walks detonations along that trail in order: your past keeps
 * exploding behind your present. Lazy orbiting gets you caught by your own
 * footsteps; sharp direction changes leave the strikes detonating on
 * ground you've genuinely abandoned. Recording stops partway through the
 * active window so every recorded footstep is consumed by the end — no
 * invisible leftovers.
 *
 * Distinct from Skyfall (snapshots of where you ARE) — this replays where
 * you WERE, in sequence, as a connected chasing fuse line.
 */
export class DejaVu extends AttackPattern {
  constructor({ minPhase = 0, config }) {
    super({ name: 'Déjà Vu', telegraphDuration: 1.1, activeDuration: 3.4, minPhase });
    this.config = config;
  }

  onBegin(_boss, player) {
    this._trail = [{ x: player.x, y: player.y }];
    this._recordTimer = 0.22;
    this._recordingFor = this.telegraphDuration + 1.2; // stop early so the trail fully drains
    this._strikes = [];
    this._strikeTimer = 0.4;
  }

  _record(dt, player) {
    if (this._recordingFor <= 0) return;
    this._recordingFor -= dt;
    this._recordTimer -= dt;
    if (this._recordTimer <= 0) {
      this._recordTimer = 0.22;
      this._trail.push({ x: player.x, y: player.y });
    }
  }

  onTelegraphTick(dt, _boss, player) {
    this._record(dt, player);
  }

  onActiveTick(dt, _boss, player, pool) {
    this._record(dt, player);

    // Arm the next footstep in recorded order.
    this._strikeTimer -= dt;
    if (this._strikeTimer <= 0 && this._trail.length) {
      this._strikeTimer = this.config.strikeInterval;
      const point = this._trail.shift();
      this._strikes.push({ x: point.x, y: point.y, fuse: this.config.fuseTime });
    }

    // Tick armed strikes down to detonation.
    for (let i = this._strikes.length - 1; i >= 0; i--) {
      const strike = this._strikes[i];
      strike.fuse -= dt;
      if (strike.fuse > 0) continue;
      this._strikes.splice(i, 1);
      if (Math.hypot(player.x - strike.x, player.y - strike.y) < this.config.strikeRadius + player.radius) {
        player.takeDamage(this.config.strikeDamage);
      }
      for (let k = 0; k < 5; k++) {
        const angle = (k / 5) * Math.PI * 2 + strike.x * 0.01;
        pool.fire({
          x: strike.x,
          y: strike.y,
          vx: Math.cos(angle) * this.config.shrapnelSpeed,
          vy: Math.sin(angle) * this.config.shrapnelSpeed,
          radius: 2,
          damage: this.config.shrapnelDamage,
          color: '#8c7bff',
          lifetime: 2.2,
        });
      }
    }
  }

  getVisual() {
    if (this.state !== PatternState.TELEGRAPH && this.state !== PatternState.ACTIVE) return null;
    return {
      kind: 'dejaVu',
      trail: this._trail,
      strikes: this._strikes.map((s) => ({ x: s.x, y: s.y, radius: this.config.strikeRadius, imminent: s.fuse < 0.25 })),
    };
  }
}
